import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth } from '@/config/firebase'
import { updateChild } from '@/services/parentService'
import { getUserSubscription, isPremiumSubscription } from '@/services/subscriptionService'
import { Button } from '@/components/ui/button'
import { Loader2, ArrowLeft, Save, Lock, Sparkles, Shuffle } from 'lucide-react'
import { toast } from 'sonner'
import { useChild } from '@/context/ChildContext'
import { createAvatar } from '@dicebear/core'
import { adventurer, bottts, funEmoji, lorelei, pixelArt, thumbs } from '@dicebear/collection'
import DiceBearAvatar from '@/components/DiceBearAvatar'

// Fun kid-friendly style configurations
const GAME_STYLES = {
  adventurer: {
    name: '🧙 Explorer',
    style: adventurer,
    bgClass: 'from-green-400 to-emerald-500',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'f4d9b2', 'c9e4ca'],
      skinColor: ['f2d3b1', 'ecad80', 'd78f5a', 'c67b48', 'a56c43', '8d5524'],
      hair: ['short01', 'short02', 'short03', 'short04', 'long01', 'long02', 'long03', 'long04', 'long05'],
      hairColor: ['0e0e0e', '3a3a3a', '6a4e35', 'b58143', 'dba520', 'ab2a1c'],
      eyes: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06'],
      mouth: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06'],
    }
  },
  bottts: {
    name: '🤖 Robot',
    style: bottts,
    bgClass: 'from-blue-400 to-indigo-500',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
      baseColor: ['00acc1', '1e88e5', '5e35b1', '8e24aa', 'd81b60', 'e53935', 'fb8c00', 'fdd835', '43a047'],
      eyes: ['bulging', 'eva', 'frame1', 'glow', 'happy', 'robocop', 'round', 'sensor'],
      face: ['square01', 'square02', 'round01', 'round02'],
      mouth: ['bite', 'grill01', 'smile01', 'smile02', 'square01'],
    }
  },
  funEmoji: {
    name: '😄 Emoji',
    style: funEmoji,
    bgClass: 'from-yellow-400 to-orange-500',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'fcee4e', 'a7e75e'],
      eyes: ['closed', 'cute', 'love', 'plain', 'shades', 'stars', 'wink'],
      mouth: ['cute', 'lilSmile', 'plain', 'smileLol', 'smileTeeth', 'tongueOut', 'wideSmile'],
    }
  },
  lorelei: {
    name: '✨ Portrait',
    style: lorelei,
    bgClass: 'from-pink-400 to-rose-500',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
      skinColor: ['f2d3b1', 'ecad80', 'd78f5a', 'c67b48', 'a56c43'],
      hair: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06', 'variant07', 'variant08'],
      hairColor: ['0e0e0e', '3a3a3a', '6a4e35', 'b58143', 'dba520', 'ab2a1c'],
      eyes: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06'],
      mouth: ['happy01', 'happy02', 'happy03', 'happy04', 'happy05', 'happy06'],
    }
  },
  pixelArt: {
    name: '👾 Pixel',
    style: pixelArt,
    bgClass: 'from-purple-400 to-violet-500',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
      skinColor: ['ecad80', 'f2d3b1', 'd78f5a', 'a56c43', '8d5524'],
      hair: ['short01', 'short02', 'short03', 'long01', 'long02', 'long03'],
      hairColor: ['0e0e0e', '6a4e35', 'b58143', 'dba520', 'ab2a1c'],
      eyes: ['variant01', 'variant02', 'variant03', 'variant04', 'variant05', 'variant06'],
      mouth: ['happy01', 'happy02', 'happy03', 'happy04', 'happy05', 'happy06'],
    }
  },
  thumbs: {
    name: '👍 Thumbs',
    style: thumbs,
    bgClass: 'from-teal-400 to-cyan-500',
    options: {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
      shapeColor: ['0a5b83', '1c799f', '69d2e7', 'f38b74', 'e23d41', 'fc909f', 'a55eea', 'f69256', 'f4d06f'],
      eyes: ['variant1W12', 'variant2W12', 'variant3W12', 'variant4W12', 'variant5W12'],
      mouth: ['variant1', 'variant2', 'variant3', 'variant4', 'variant5'],
    }
  },
} as const

type GameStyleId = keyof typeof GAME_STYLES

interface GameCharacterConfig {
  style: GameStyleId
  seed: string
  options: Record<string, string | number>
}

function generateSeed(): string {
  return Math.random().toString(36).substring(2, 10)
}

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function generateRandomOptions(styleId: GameStyleId): Record<string, string> {
  const styleConfig = GAME_STYLES[styleId]
  const options: Record<string, string> = {}
  for (const [key, values] of Object.entries(styleConfig.options)) {
    options[key] = pickRandom(values)
  }
  return options
}

function parseGameCharacterConfig(saved: string | undefined): GameCharacterConfig {
  const defaultStyle: GameStyleId = 'adventurer'
  if (!saved) {
    return { style: defaultStyle, seed: generateSeed(), options: generateRandomOptions(defaultStyle) }
  }
  try {
    const parsed = JSON.parse(saved)
    const style = (parsed.style in GAME_STYLES ? parsed.style : defaultStyle) as GameStyleId
    return {
      style,
      seed: parsed.seed || generateSeed(),
      options: parsed.options || generateRandomOptions(style)
    }
  } catch {
    return { style: defaultStyle, seed: generateSeed(), options: generateRandomOptions(defaultStyle) }
  }
}

function generateCharacterUri(config: GameCharacterConfig, size = 200): string {
  const styleConfig = GAME_STYLES[config.style]
  if (!styleConfig) return ''
  
  const dicebearOptions: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(config.options)) {
    dicebearOptions[key] = typeof value === 'string' ? [value] : value
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createAvatar(styleConfig.style as any, {
    seed: config.seed,
    size,
    ...dicebearOptions,
  }).toDataUri()
}

export default function CharacterCreationPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasAccess, setHasAccess] = useState(false)
  const { childrenProfiles, activeChildId, setActiveChildId, refreshChildren } = useChild()
  
  const [selectedChildId, setSelectedChildId] = useState<string | null>(activeChildId)
  const [config, setConfig] = useState<GameCharacterConfig | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [useThea, setUseThea] = useState(false)

  // Check premium access
  useEffect(() => {
    const checkAccess = async () => {
      if (!auth.currentUser) {
        navigate('/subscription')
        return
      }
      
      const subscription = await getUserSubscription(auth.currentUser.uid)
      if (isPremiumSubscription(subscription)) {
        setHasAccess(true)
        setLoading(false)
      } else {
        navigate('/subscription')
      }
    }
    checkAccess()
  }, [navigate])

  // Load config when child changes
  useEffect(() => {
    if (!hasAccess) return

    const childId = activeChildId || (childrenProfiles.length > 0 ? childrenProfiles[0].id : null)
    if (childId && childId !== selectedChildId) {
      setSelectedChildId(childId)
      setInitialized(false)
    }
  }, [activeChildId, childrenProfiles, hasAccess, selectedChildId])

  // Initialize config from selected child
  useEffect(() => {
    if (!selectedChildId || initialized) return
    
    const child = childrenProfiles.find(c => c.id === selectedChildId)
    if (child) {
      // Use gameCharacterConfig if available, otherwise show Thea as default
      const gameConfig = (child as { gameCharacterConfig?: string }).gameCharacterConfig
      if (!gameConfig) {
        // No custom character - using Thea
        setUseThea(true)
        setConfig(parseGameCharacterConfig(undefined))
      } else {
        setUseThea(false)
        setConfig(parseGameCharacterConfig(gameConfig))
      }
      setInitialized(true)
    }
  }, [selectedChildId, childrenProfiles, initialized])

  const characterUri = useMemo(() => {
    if (!config) return ''
    return generateCharacterUri(config, 256)
  }, [config])

  const handleChildSelect = (childId: string) => {
    setSelectedChildId(childId)
    setActiveChildId(childId)
    setInitialized(false)
  }

  const handleStyleChange = (styleId: GameStyleId) => {
    setUseThea(false)
    setConfig({
      style: styleId,
      seed: generateSeed(),
      options: generateRandomOptions(styleId)
    })
  }

  const handleRandomize = () => {
    if (!config) return
    setConfig({
      ...config,
      seed: generateSeed(),
      options: generateRandomOptions(config.style)
    })
  }

  const handleOptionChange = (key: string, value: string) => {
    if (!config) return
    setConfig({
      ...config,
      options: { ...config.options, [key]: value }
    })
  }

  const handleSave = async () => {
    if (!selectedChildId || !auth.currentUser) return

    setSaving(true)
    try {
      await updateChild(auth.currentUser.uid, selectedChildId, {
        // If using Thea, clear the config; otherwise save the custom character
        gameCharacterConfig: useThea ? '' : JSON.stringify(config)
      })
      
      await refreshChildren()
      toast.success(useThea ? '🧜‍♀️ Using Thea the Mermaid!' : '🎉 Character saved! Ready for games!')
      navigate('/')
    } catch (error) {
      console.error('Error saving character:', error)
      toast.error('Failed to save character.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-100 to-purple-100">
        <Loader2 className="w-12 h-12 animate-spin text-medium-teal" />
        <p className="mt-4 text-lg text-slate-600">Loading...</p>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-100 to-purple-100">
        <Lock className="w-20 h-20 text-medium-purple mb-4" />
        <h2 className="text-3xl font-bold text-slate-800 mb-2">Premium Feature</h2>
        <p className="text-lg text-slate-600 mb-6">Subscribe to unlock Character Creator!</p>
        <Button onClick={() => navigate('/subscription')} size="lg" className="bg-medium-purple hover:bg-deep-purple text-lg px-8">
          View Plans
        </Button>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-medium-teal" />
      </div>
    )
  }

  const currentStyle = GAME_STYLES[config.style]

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-purple-50 to-pink-100 py-6 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-yellow-500" />
              Create Your Character!
            </h1>
            <p className="text-slate-600">Make a character to use in games</p>
          </div>
        </div>

        {/* Child Selector */}
        {childrenProfiles.length > 1 && (
          <div className="bg-white/80 backdrop-blur rounded-2xl p-4 mb-6 shadow-lg">
            <p className="text-sm font-medium text-slate-600 mb-2">Who's playing?</p>
            <div className="flex flex-wrap gap-2">
              {childrenProfiles.map(child => (
                <button
                  key={child.id}
                  onClick={() => handleChildSelect(child.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full transition-all text-sm font-medium
                    ${selectedChildId === child.id 
                      ? 'bg-medium-teal text-white shadow-md scale-105' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}
                  `}
                >
                  <div className="w-6 h-6 rounded-full overflow-hidden">
                    <DiceBearAvatar config={child.avatarConfig} fallbackSeed={child.displayName} size={24} />
                  </div>
                  {child.displayName}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-[280px,1fr] gap-6">
          {/* LEFT - Character Preview */}
          <div className="md:sticky md:top-24 md:self-start space-y-4">
            <div className={`bg-gradient-to-br ${useThea ? 'from-cyan-400 to-teal-500' : currentStyle.bgClass} rounded-3xl p-6 shadow-xl`}>
              <div className="bg-white/30 backdrop-blur rounded-2xl p-4">
                <div className="w-56 h-56 mx-auto rounded-full overflow-hidden bg-white shadow-xl border-4 border-white">
                  {useThea ? (
                    <img src="/thea-mermaid-logo-transparent.png" alt="Thea the Mermaid" className="w-full h-full object-cover" />
                  ) : (
                    <img src={characterUri} alt="Your character" className="w-full h-full object-cover" />
                  )}
                </div>
              </div>
              {!useThea && (
                <Button 
                  onClick={handleRandomize} 
                  variant="secondary" 
                  size="lg" 
                  className="w-full mt-4 bg-white/90 hover:bg-white text-slate-800 font-bold"
                >
                  <Shuffle className="w-5 h-5 mr-2" />
                  Surprise Me!
                </Button>
              )}
              {useThea && (
                <p className="text-center text-white font-bold mt-4 text-lg">🧜‍♀️ Thea the Mermaid</p>
              )}
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              size="lg"
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-lg font-bold shadow-lg"
            >
              {saving ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-5 h-5 mr-2" /> {useThea ? 'Use Thea!' : 'Save Character!'}</>
              )}
            </Button>

            {/* Use Thea Toggle */}
            <button
              onClick={() => setUseThea(!useThea)}
              className={`
                w-full py-3 px-4 rounded-xl border-2 transition-all font-medium flex items-center justify-center gap-2
                ${useThea 
                  ? 'bg-cyan-100 border-cyan-400 text-cyan-700' 
                  : 'bg-white/80 border-slate-200 text-slate-600 hover:border-cyan-300 hover:bg-cyan-50'}
              `}
            >
              <span className="text-xl">🧜‍♀️</span>
              {useThea ? 'Create your own character' : 'Use Thea the Mermaid instead'}
            </button>
          </div>

          {/* RIGHT - Customization */}
          <div className={`space-y-4 transition-all ${useThea ? 'opacity-40 pointer-events-none' : ''}`}>
            {/* Style Picker */}
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5 shadow-lg">
              <h3 className="text-lg font-bold text-slate-800 mb-3">Pick a Style</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {Object.entries(GAME_STYLES).map(([id, style]) => {
                  const isSelected = config.style === id && !useThea
                  const previewConfig: GameCharacterConfig = { 
                    style: id as GameStyleId, 
                    seed: 'preview',
                    options: {}
                  }
                  const previewUri = generateCharacterUri(previewConfig, 64)
                  
                  return (
                    <button
                      key={id}
                      onClick={() => handleStyleChange(id as GameStyleId)}
                      disabled={useThea}
                      className={`
                        p-2 rounded-xl border-3 transition-all hover:scale-105
                        ${isSelected 
                          ? 'border-medium-teal bg-medium-teal/10 shadow-lg scale-105' 
                          : 'border-gray-200 bg-white hover:border-gray-300'}
                      `}
                    >
                      <div className="w-12 h-12 mx-auto rounded-full overflow-hidden bg-gray-50">
                        <img src={previewUri} alt={style.name} className="w-full h-full object-cover" />
                      </div>
                      <p className={`text-xs font-bold mt-1 text-center ${isSelected ? 'text-medium-teal' : 'text-slate-600'}`}>
                        {style.name}
                      </p>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Feature Pickers */}
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5 shadow-lg">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Customize Your Look</h3>
              <div className="space-y-5">
                {Object.entries(currentStyle.options).map(([optionKey, values]) => {
                  const currentValue = config.options[optionKey]
                  const isColor = optionKey.toLowerCase().includes('color')
                  const label = optionKey
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .replace('Color', ' Color')
                  
                  return (
                    <div key={optionKey}>
                      <p className="text-sm font-semibold text-slate-700 mb-2">{label}</p>
                      {isColor ? (
                        <div className="flex flex-wrap gap-2">
                          {values.map((color: string) => (
                            <button
                              key={color}
                              onClick={() => handleOptionChange(optionKey, color)}
                              disabled={useThea}
                              className={`
                                w-10 h-10 rounded-full border-3 transition-all hover:scale-110
                                ${currentValue === color 
                                  ? 'border-slate-800 ring-2 ring-medium-teal ring-offset-2 scale-110' 
                                  : 'border-white shadow-md'}
                              `}
                              style={{ backgroundColor: `#${color}` }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {values.map((variant: string, idx: number) => (
                            <button
                              key={variant}
                              onClick={() => handleOptionChange(optionKey, variant)}
                              disabled={useThea}
                              className={`
                                w-10 h-10 rounded-lg font-bold transition-all hover:scale-105
                                ${currentValue === variant 
                                  ? 'bg-medium-teal text-white shadow-md scale-105' 
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}
                              `}
                            >
                              {idx + 1}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
